import React from 'react';
import { Award, Star, Lock, Utensils, Users, Compass, Crown, Globe, Heart, Beer, Moon, CheckCircle, Leaf, Landmark, Trophy } from 'lucide-react';
import type { Achievement } from '../lib/achievements';
import { useLanguage } from '../contexts/LanguageContext';

interface AchievementsProps {
  achievements: Achievement[];
}

const Achievements: React.FC<AchievementsProps> = ({ achievements }) => {
  const { t } = useLanguage();
  
  // Group achievements by category
  const groupedAchievements = achievements.reduce((acc, achievement) => {
    if (!acc[achievement.category]) {
      acc[achievement.category] = [];
    }
    acc[achievement.category].push(achievement);
    return acc;
  }, {} as Record<string, Achievement[]>);

  // Map of icons for each category
  const categoryIcons = {
    milestone: Award,
    dining: Utensils,
    social: Users,
    exploration: Compass,
    special: Crown
  };

  // Map of colors for each category
  const categoryColors = {
    milestone: 'yellow',
    dining: 'orange',
    social: 'blue',
    exploration: 'purple',
    special: 'emerald'
  };

  // Map of icons for specific achievements
  const achievementIcons = {
    'award': Award,
    'calendar': Globe,
    'star': Star,
    'users': Users,
    'network': Users,
    'heart': Heart,
    'utensils': Utensils,
    'compass': Compass,
    'chef-hat': Crown,
    'crown': Crown,
    'beer': Beer,
    'moon': Moon,
    'globe': Globe,
    'sparkles': Star,
    'leaf': Leaf,
    'check-circle': CheckCircle,
    'landmark': Landmark
  };

  // Map of colors for difficulty levels
  const difficultyColors = {
    easy: 'green',
    medium: 'blue',
    hard: 'purple',
    epic: 'orange',
    legendary: 'red'
  };

  // Calculate total points for a category
  const calculateCategoryPoints = (categoryAchievements: Achievement[]) => {
    const earned = categoryAchievements.reduce((total, achievement) => 
      total + (achievement.unlocked ? achievement.points : 0), 0);
    const total = categoryAchievements.reduce((total, achievement) => 
      total + achievement.points, 0);
    return { earned, total };
  };

  return (
    <div className="space-y-8">
      {Object.entries(groupedAchievements).map(([category, categoryAchievements]) => {
        const CategoryIcon = categoryIcons[category as keyof typeof categoryIcons];
        const color = categoryColors[category as keyof typeof categoryColors];
        const { earned, total } = calculateCategoryPoints(categoryAchievements);
        
        return (
          <div key={category} className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CategoryIcon className={`h-6 w-6 text-${color}-500`} />
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {category} Achievements
                </h3>
              </div>
              <div className="flex items-center space-x-2">
                <Trophy className={`h-5 w-5 text-${color}-500`} />
                <span className="text-sm font-medium text-gray-600">
                  {earned} / {total} points
                </span>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryAchievements.map((achievement) => {
                const AchievementIcon = achievementIcons[achievement.icon as keyof typeof achievementIcons] || Award;
                const difficultyColor = difficultyColors[achievement.difficulty];
                
                return (
                  <div
                    key={achievement.id}
                    className={`bg-white rounded-xl p-6 shadow-sm transform transition-all duration-300 hover:shadow-lg ${
                      achievement.unlocked 
                        ? `border-2 border-${color}-200 hover:border-${color}-300` 
                        : 'opacity-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-gray-900">{achievement.name}</h4>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full bg-${difficultyColor}-100 text-${difficultyColor}-700`}>
                            {achievement.points} pts
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{achievement.description}</p>
                      </div>
                      {achievement.unlocked ? (
                        <div className={`p-2 bg-${color}-100 rounded-full`}>
                          <AchievementIcon className={`h-5 w-5 text-${color}-500`} />
                        </div>
                      ) : (
                        <div className="p-2 bg-gray-100 rounded-full">
                          <Lock className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {achievement.progress !== undefined && achievement.maxProgress !== undefined && (
                      <div className="mt-4">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <span>Fortschritt</span>
                          <span>{achievement.progress}/{achievement.maxProgress}</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full bg-${color}-500 transition-all duration-500`}
                            style={{ 
                              width: `${Math.min(100, (achievement.progress / achievement.maxProgress) * 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="mt-4">
                      <div className="text-xs text-gray-500 font-medium">Kriterien:</div>
                      <ul className="mt-1 text-xs text-gray-600 space-y-1">
                        {achievement.criteria.map((criterion, index) => (
                          <li key={index} className="flex items-center space-x-1">
                            <CheckCircle className={`h-3 w-3 ${achievement.unlocked ? 'text-green-500' : 'text-gray-400'}`} />
                            <span>{criterion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Achievements;